import React, { Fragment, Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { submit, change } from 'redux-form';

import Snackbar from 'components/common/Snackbar';
import Button from 'components/common/Button';

import objectDiff from 'utils/objectDiff';
import formatSinglePlural from 'utils/formatSinglePlural';
import formatOrderNumber from 'utils/formatOrderNumber';

import { fetchConnectedOrders } from 'redux/modules/connectedOrders';
import { addNotification } from 'redux/modules/notifications';

import ProductClaimViewHeader from './ProductClaimViewHeader';
import ProductClaimViewForm from './ProductClaimViewForm';

class ProductClaimView extends Component {
  constructor(props) {
    super(props);

    this.state = {
      groupedUpdateValues: null,
      groupedUpdateConnectedIDs: null,
      groupedUpdateNoDateofDeliveryIDs: null,
      groupedUpdateNoDriverIDs: null,
      snackbarOpen: false,
    };

    this.groupedUpdate = false;
    this.syncUpdate = false;

    this.onFormChange = this.onFormChange.bind(this);
    this.onFormSubmitSuccess = this.onFormSubmitSuccess.bind(this);
    this.onGroupedUpdateConfirm = this.onGroupedUpdateConfirm.bind(this);
    this.onSnackbarClose = this.onSnackbarClose.bind(this);
  }

  onFormSubmitSuccess(values, dispatch, props) {
    const { ID, actions, onSubmitSuccess, ...otherProps } = this.props;
    const {
      groupedUpdateNoDateofDeliveryIDs,
      groupedUpdateNoDriverIDs,
    } = this.state;
    const connectedIDs = values
      .filter(orderLine => ['Recurring', 'Rental'].indexOf(orderLine.Type) !== -1)
      .filter((orderLine) => {
        const diff = objectDiff(props['Order Line'].find(item => item.ID === orderLine.ID), orderLine);
        delete diff.Status;
        delete diff.State;
        return Object.keys(diff).length > 0;
      })
      .map(orderLine => orderLine.ID);
    let noDateofDeliveryIDs = values
      .filter(orderLine => (orderLine['Workflow Status'].filter(statusItem =>
        statusItem['Workflow State'].ID === 2).length > 0
        && !orderLine['Date of Delivery']))
      .map(orderLine => orderLine.ID);

    let noDriverIDs = values
      .filter(orderLine => (orderLine['Workflow Status'].filter(statusItem =>
        statusItem['Workflow State'].ID === 2).length > 0
        && !orderLine.Driver.ID))
      .map(orderLine => orderLine.ID);

    if (groupedUpdateNoDateofDeliveryIDs) {
      noDateofDeliveryIDs = [
        ...noDateofDeliveryIDs,
        ...groupedUpdateNoDateofDeliveryIDs,
      ];
    }
    if (groupedUpdateNoDriverIDs) {
      noDriverIDs = [
        ...noDriverIDs,
        ...groupedUpdateNoDriverIDs,
      ];
    }
    if (this.state.groupedUpdateValues) {
      this.setState({
        snackbarOpen: true,
        groupedUpdateConnectedIDs: connectedIDs.length > 0 ? connectedIDs : null,
        groupedUpdateNoDateofDeliveryIDs:
          noDateofDeliveryIDs.length > 0 ? noDateofDeliveryIDs : null,
        groupedUpdateNoDriverIDs:
          noDriverIDs.length > 0 ? noDriverIDs : null,
      });
    } else if (connectedIDs.length > 0) {
      actions.fetchConnectedOrders({
        triggerLoading: false,
        orderLineID: connectedIDs,
        orderID: ID,
      });
    } else {
      if (noDateofDeliveryIDs.length > 0) {
        const noDateofDeliveryHCPCCodes = otherProps['Order Line']
          .filter(item => noDateofDeliveryIDs.indexOf(item.ID) !== -1)
          .map(item => item['HCPC S Code']['HCPC S Code'])
          .join(', ');
        actions.addNotification({
          message: `${formatSinglePlural({
            quantity: noDateofDeliveryIDs.length,
            single: 'Code',
            plural: 'Codes',
          })} ${noDateofDeliveryHCPCCodes} in Order ${formatOrderNumber(ID)} ${formatSinglePlural({
            quantity: noDateofDeliveryIDs.length,
            single: 'has',
            plural: 'have',
          })} no Date of Delivery specified`,
          variant: 'warning',
          actionHref: `/workflow/order/edit/${ID}`,
          actionText: 'Edit',
        });
      }
      if (noDriverIDs.length > 0) {
        const noDriverHCPCCodes = otherProps['Order Line']
          .filter(item => noDriverIDs.indexOf(item.ID) !== -1)
          .map(item => item['HCPC S Code']['HCPC S Code'])
          .join(', ');
        actions.addNotification({
          message: `${formatSinglePlural({
            quantity: noDriverIDs.length,
            single: 'Code',
            plural: 'Codes',
          })} ${noDriverHCPCCodes} in Order ${formatOrderNumber(ID)} ${formatSinglePlural({
            quantity: noDriverIDs.length,
            single: 'has',
            plural: 'have',
          })} no Driver assigned`,
          variant: 'warning',
          actionHref: `/workflow/order/edit/${ID}`,
          actionText: 'Edit',
        });
      }
      this.setState({
        groupedUpdateNoDateofDeliveryIDs: null,
        groupedUpdateNoDriverIDs: null,
      });
      onSubmitSuccess(values);
    }
  }

  onFormChange(values, dispatch, props, prevValues) {
    const { ID, actions, syncUpdates, ...otherProps } = this.props;
    const diffValues = objectDiff(values, prevValues);
    const diffValuesOrderLine = Object.keys(diffValues)[0];
    Object.keys(values).forEach((orderLine) => {
      if (values[orderLine]['Assigned User ID']
        && values[orderLine]['Selected Assigned User Name']
          !== values[orderLine]['Assigned User Name']) {
        dispatch(props.change(`${orderLine}.Assigned User ID`, null));
        dispatch(props.change(`${orderLine}.Selected Assigned User Name`, null));
      }

      if (values[orderLine]['Driver ID']
        && values[orderLine]['Selected Driver Name']
          !== values[orderLine]['Driver Name']) {
        dispatch(props.change(`${orderLine}.Driver ID`, null));
        dispatch(props.change(`${orderLine}.Selected Driver Name`, null));
      }
    });

    if (Object.keys(diffValues[diffValuesOrderLine]).length > 0) {
      if (syncUpdates && !this.syncUpdate) {
        const selectedChanged = diffValues[diffValuesOrderLine].selected !== undefined;
        const selectedOrderLines = Object.keys(values)
          .filter(key => values[key].selected === true)
          .filter(key => key !== diffValuesOrderLine);
        if (!selectedChanged) {
          this.syncUpdate = true;
          selectedOrderLines.forEach((orderLine) => {
            Object.keys(diffValues[diffValuesOrderLine]).forEach((diffValuesOrderLineKey) => {
              if (diffValuesOrderLineKey === 'Status') {
                dispatch(props.change(`${orderLine}.${diffValuesOrderLineKey}`,
                  values[diffValuesOrderLine][diffValuesOrderLineKey]));
              } else {
                dispatch(props.change(`${orderLine}.${diffValuesOrderLineKey}`,
                  diffValues[diffValuesOrderLine][diffValuesOrderLineKey]));
              }
            });
          });
          setTimeout(() => {
            this.syncUpdate = false;
          }, 400);
        }
      } else {
        const statusChanged = diffValues[diffValuesOrderLine].Status;
        const assignedUserChanged = diffValues[diffValuesOrderLine]['Assigned User ID'];
        const driverChanged = diffValues[diffValuesOrderLine]['Driver ID'] !== undefined;

        if (!props.pristine
          && diffValues[diffValuesOrderLine].selected === undefined
          && (statusChanged || assignedUserChanged || driverChanged)) {
          if (!this.groupedUpdate) {
            if (otherProps['Order Line'].length > 1
              && !syncUpdates) {
              this.setState({
                groupedUpdateValues: {
                  ...diffValues[diffValuesOrderLine],
                  ...statusChanged ? { Status: values[diffValuesOrderLine].Status } : {},
                },
              });
            }
            setTimeout(() => {
              actions.submit(props.form);
            }, 400);
          }
        }
      }
    }
  }

  onGroupedUpdateConfirm() {
    const { ID, actions, form, ...otherProps } = this.props;
    const { groupedUpdateValues } = this.state;
    this.groupedUpdate = true;
    this.setState({
      groupedUpdateValues: null,
      snackbarOpen: false,
    });
    otherProps['Order Line'].forEach((orderLine) => {
      Object.keys(groupedUpdateValues).forEach((filedName) => {
        actions.change(form, `${orderLine.ID}-orderLine.${filedName}`, groupedUpdateValues[filedName]);
      });
    });
    // Delay to wait multiple async change actions to complete
    setTimeout(() => {
      actions.submit(form);
      this.groupedUpdate = false;
    }, 200);
  }

  onSnackbarClose() {
    const { ID, actions, ...otherProps } = this.props;
    const {
      groupedUpdateConnectedIDs,
      groupedUpdateNoDateofDeliveryIDs,
      groupedUpdateNoDriverIDs,
    } = this.state;
    const newState = {
      groupedUpdateValues: null,
      groupedUpdateConnectedIDs: null,
      snackbarOpen: false,
    };
    if (groupedUpdateConnectedIDs) {
      actions.fetchConnectedOrders({
        triggerLoading: false,
        orderLineID: groupedUpdateConnectedIDs,
        orderID: ID,
      });
    }
    if (groupedUpdateNoDateofDeliveryIDs && !this.groupedUpdate) {
      const noDateofDeliveryHCPCCodes = otherProps['Order Line']
        .filter(item => groupedUpdateNoDateofDeliveryIDs.indexOf(item.ID) !== -1)
        .map(item => item['HCPC S Code']['HCPC S Code'])
        .join(', ');
      actions.addNotification({
        message: `${formatSinglePlural({
          quantity: groupedUpdateNoDateofDeliveryIDs.length,
          single: 'Code',
          plural: 'Codes',
        })} ${noDateofDeliveryHCPCCodes} in Order ${formatOrderNumber(ID)} ${formatSinglePlural({
          quantity: groupedUpdateNoDateofDeliveryIDs.length,
          single: 'has',
          plural: 'have',
        })} no Date of Delivery specified`,
        variant: 'warning',
        actionHref: `/workflow/order/edit/${ID}`,
        actionText: 'Edit',
      });
      newState.groupedUpdateDeliveryIDs = null;
    }
    if (groupedUpdateNoDriverIDs && !this.groupedUpdate) {
      const noDriverHCPCCodes = otherProps['Order Line']
        .filter(item => groupedUpdateNoDriverIDs.indexOf(item.ID) !== -1)
        .map(item => item['HCPC S Code']['HCPC S Code'])
        .join(', ');
      actions.addNotification({
        message: `${formatSinglePlural({
          quantity: groupedUpdateNoDriverIDs.length,
          single: 'Code',
          plural: 'Codes',
        })} ${noDriverHCPCCodes} in Order ${formatOrderNumber(ID)} ${formatSinglePlural({
          quantity: groupedUpdateNoDriverIDs.length,
          single: 'has',
          plural: 'have',
        })} no Driver assigned`,
        variant: 'warning',
        actionHref: `/workflow/order/edit/${ID}`,
        actionText: 'Edit',
      });
      newState.groupedUpdateNoDriverIDs = null;
    }
    this.setState(newState);
  }

  render() {
    const { form, disabled } = this.props;
    const { snackbarOpen } = this.state;
    return (
      <Fragment>
        <ProductClaimViewHeader {...this.props} />
        <ProductClaimViewForm
          form={form}
          {...this.props}
          initialValues={this.props['Order Line'].reduce((accumulator, currentValue) => ({
            ...accumulator,
            [`${currentValue.ID}-orderLine`]: {
              Status: currentValue['Workflow Status'].map(statusItem => ({
                value: statusItem.ID,
                label: statusItem.Name,
              })),
              'Assigned User ID': currentValue['Assigned User'].ID,
              'Assigned User Name': `${currentValue['Assigned User']['First Name']} ${currentValue['Assigned User']['Last Name']}`,
              'Selected Assigned User Name': `${currentValue['Assigned User']['First Name']} ${currentValue['Assigned User']['Last Name']}`,
              ...currentValue.Driver.ID ? {
                'Driver ID': currentValue.Driver.ID,
                'Driver Name': `${currentValue.Driver['First Name']} ${currentValue.Driver['Last Name']}`,
                'Selected Driver Name': `${currentValue.Driver['First Name']} ${currentValue.Driver['Last Name']}`,
              } : {},
            },
          }), {})}
          onChange={this.onFormChange}
          onSubmitSuccess={this.onFormSubmitSuccess}
          onTextFieldBlur={this.onFormTextFieldBlur}
          disabled={disabled}
        />
        <Snackbar
          open={snackbarOpen}
          variant="success"
          message="Order successfully updated. Apply update to other order lines?"
          autoHideDuration={3000}
          resumeHideDuration={0}
          onClose={this.onSnackbarClose}
          action={(
            <Button
              variant="raised"
              color="primary"
              onClick={this.onGroupedUpdateConfirm}
            >
              Apply
            </Button>
          )}
        />
      </Fragment>
    );
  }
}

ProductClaimView.propTypes = {
  form: PropTypes.string.isRequired,
  ID: PropTypes.number.isRequired,
  'Order Line': PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number.isRequired,
  })).isRequired,
  syncUpdates: PropTypes.bool,
  onSubmitSuccess: PropTypes.func,
  disabled: PropTypes.bool,
  actions: PropTypes.shape({
    fetchConnectedOrders: PropTypes.func.isRequired,
    addNotification: PropTypes.func.isRequired,
    submit: PropTypes.func.isRequired,
    change: PropTypes.func.isRequired,
  }).isRequired,
};

ProductClaimView.defaultProps = {
  buttonDisabled: false,
  syncUpdates: false,
  disabled: false,
  onSubmitSuccess: () => {},
};


const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    fetchConnectedOrders,
    addNotification,
    submit,
    change,
  }, dispatch),
});

export default connect(null, mapDispatchToProps)(
  ProductClaimView,
);



// WEBPACK FOOTER //
// ./src/components/workflow/ProductClaimView/ProductClaimView.js